import React, { useState, useEffect, lazy, Suspense, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, TrendingUp, TrendingDown, Target, Flame, 
  ChevronRight, Sparkles, Calendar, PiggyBank,
  Home, Wallet, BarChart3, Settings, Award, Camera, Users, User,
  ShoppingCart, Mail, FileText, PieChart, RefreshCw, Mic, FileBarChart, Shield,
  Gift, Clock, CheckCircle, XCircle, Send, LogOut,
  ShoppingBag, Gamepad2, Candy, Shirt, Music, Book,
  Eye, EyeOff, X, ChevronUp, Zap, Calculator, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  getTransactions, getBudgets, getObjectives, getUserStats, getTodayChallenge, 
  getFamily, processAutoAllowance, getPausedObjectives, processSubscriptions,
  getPocketMoneyBalance, requestPocketMoney, getPocketMoneyRequests, 
  getChildTransactions, addChildExpense, getFamilyStats, getMonthlyIncomeTotal
} from '../lib/api';
import { formatCurrency, formatDateRelative, getCategoryColor, calculatePercentage, getXPForNextLevel } from '../lib/utils';
import { checkAndNotifyPausedObjectives, getNotificationPermission } from '../lib/notifications';
import { runSubscriptionReminders } from '../lib/subscriptionReminders';
import BottomNav from '../components/BottomNav';
import LanguageSelector from '../components/LanguageSelector';
import { toast } from 'sonner';

// Lazy load heavy modals
const QuickAddModal = lazy(() => import('../components/QuickAddModal'));
const QuickAddIncomeModal = lazy(() => import('../components/QuickAddIncomeModal'));
const OCRScannerModal = lazy(() => import('../components/OCRScannerModal'));
const VoiceAssistant = lazy(() => import('../components/VoiceAssistant'));
const ChallengeCard = lazy(() => import('../components/ChallengeCard'));
const AlertsPanel = lazy(() => import('../components/AlertsPanel'));
const NegativeBalanceModal = lazy(() => import('../components/NegativeBalanceModal'));
const OnboardingTutorial = lazy(() => import('../components/OnboardingTutorial').then(m => ({ default: m.default })));
const ExpenseSimulator = lazy(() => import('../components/ExpenseSimulator'));
const IncomeEvolutionChart = lazy(() => import('../components/IncomeEvolutionChart'));
const VariableIncomeBreakdownModal = lazy(() => import('../components/VariableIncomeBreakdownModal'));
import { useTutorial } from '../components/OnboardingTutorial';

// Child-friendly categories with icons
const CHILD_CATEGORIES = {
  'bonbons': { icon: Candy, color: 'text-pink-500', bg: 'bg-pink-100 dark:bg-pink-900/30', label: 'Bonbons' },
  'jeux': { icon: Gamepad2, color: 'text-purple-500', bg: 'bg-purple-100 dark:bg-purple-900/30', label: 'Jeux' },
  'vetements': { icon: Shirt, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Vêtements' },
  'musique': { icon: Music, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Musique' },
  'livres': { icon: Book, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30', label: 'Livres' },
  'cadeaux': { icon: Gift, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Cadeaux' },
  'autre': { icon: ShoppingBag, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-900/30', label: 'Autre' },
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const { shouldShow: showTutorial } = useTutorial();
  
  // Check if user is a child - adapt dashboard accordingly
  const isChild = user?.family_role === 'child';
  // Check if user is single - hide family features
  const isSingle = user?.situation === 'single';
  const shouldHideFamily = isChild || isSingle;
  
  // Common states
  const [stats, setStats] = useState(null);
  const [familyStats, setFamilyStats] = useState(null); // Stats from /api/family/stats for accurate balance
  const [monthlyIncome, setMonthlyIncome] = useState(null); // From /api/income/monthly-total (true total including daily/one_time)
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [challenge, setChallenge] = useState(null);
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showQuickAddIncome, setShowQuickAddIncome] = useState(false);
  const [showVarBreakdown, setShowVarBreakdown] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [showSimulator, setShowSimulator] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  
  // Privacy: hide/show balance amounts
  const [hideBalances, setHideBalances] = useState(() => {
    // Persist preference in localStorage
    return localStorage.getItem('spenspot_hide_balances') === 'true';
  });
  
  // Toggle balance visibility and save preference
  const toggleBalanceVisibility = () => {
    const newValue = !hideBalances;
    setHideBalances(newValue);
    localStorage.setItem('spenspot_hide_balances', newValue.toString());
  };
  
  // Helper function to mask currency amounts
  const maskAmount = (amount) => {
    if (hideBalances) {
      return '••••••';
    }
    return formatCurrency(amount);
  };
  
  // Child-specific states
  const [pocketMoneyBalance, setPocketMoneyBalance] = useState(0);
  const [pocketMoneyAllowance, setPocketMoneyAllowance] = useState(null);
  const [allowanceFrequency, setAllowanceFrequency] = useState(null);
  const [pocketMoneyRequests, setPocketMoneyRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  
  // FAB menu state
  const [fabExpanded, setFabExpanded] = useState(false);

  useEffect(() => {
    loadData();
  }, [user?.family_role]); // Reload when family_role changes
  
  useEffect(() => {
    // Show tutorial after a short delay (adults only)
    if (showTutorial && !isChild) {
      setTimeout(() => setTutorialActive(true), 1000);
    }
    
    // Listen for voice command to open OCR scanner
    const handleOpenOCR = () => setShowOCR(true);
    window.addEventListener('openOCRScanner', handleOpenOCR);
    return () => window.removeEventListener('openOCRScanner', handleOpenOCR);
  }, [showTutorial, isChild]);

  const loadData = async (isRefresh = false) => {
    try {
      // Only show loading indicator on initial load, not on refresh
      if (!isRefresh) {
        setLoading(true);
      }
      
      // Load all common data in parallel - single batch
      const [statsRes, txnRes, familyRes, incomeRes] = await Promise.all([
        getUserStats(),
        getTransactions({ limit: isChild ? 10 : 5 }),
        getFamily().catch(() => ({ data: { family: null } })),
        getMonthlyIncomeTotal().catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setTransactions(txnRes.data.transactions);
      setFamily(familyRes.data.family);
      setMonthlyIncome(incomeRes.data);
      
      // Load child-specific data
      if (isChild) {
        try {
          const [balanceRes, requestsRes] = await Promise.all([
            getPocketMoneyBalance(),
            getPocketMoneyRequests()
          ]);
          
          if (balanceRes.data.is_child) {
            setPocketMoneyBalance(balanceRes.data.balance || 0);
            setPocketMoneyAllowance(balanceRes.data.allowance);
            setAllowanceFrequency(balanceRes.data.allowance_frequency);
          }
          setPocketMoneyRequests(requestsRes.data.requests || []);
        } catch (e) {
          console.log('[Dashboard] Child data load error:', e);
        }
      } else {
        // Load adult-specific data - ALL in parallel
        const promises = [
          getBudgets(),
          getObjectives(),
          getTodayChallenge(),
        ];
        
        // Add family-related promises if user is in a family
        if (familyRes.data.family) {
          promises.push(
            getFamilyStats().catch(() => ({ data: null })),
            processAutoAllowance().catch(() => ({ data: { processed: 0 } }))
          );
        }
        
        // Add subscription processing
        promises.push(processSubscriptions().catch(() => ({ data: { processed: 0 } })));
        
        const results = await Promise.all(promises);
        
        // Set states from results
        setBudgets(results[0].data);
        setObjectives(results[1].data);
        setChallenge(results[2].data);
        
        if (familyRes.data.family && results[3]?.data) {
          setFamilyStats(results[3].data);
        }
        
        // Check for paused objectives notification (only on initial load, async - don't wait)
        if (getNotificationPermission() === 'granted' && !isRefresh) {
          getPausedObjectives().then(async (pausedRes) => {
            if (pausedRes.data.paused_objectives?.length > 0) {
              try {
                const { getNotificationPreferences } = await import('../lib/api');
                const prefsRes = await getNotificationPreferences();
                const reminderDays = prefsRes.data?.paused_objectives_reminder_enabled !== false 
                  ? (prefsRes.data?.paused_objectives_reminder_days || 5) 
                  : 0;
                if (reminderDays > 0) {
                  checkAndNotifyPausedObjectives(pausedRes.data.paused_objectives, language, reminderDays);
                }
              } catch (e) { /* ignore */ }
            }
          }).catch(() => {});

          // Subscription payment reminders (push notifications) — flexible-frequency aware
          runSubscriptionReminders({ language }).catch(() => {});
        }
      }
    } catch (error) {
      console.error('[Dashboard] Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Child-specific: Handle pocket money request
  const handleRequestPocketMoney = async () => {
    const amount = parseFloat(requestAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('enter_valid_amount'));
      return;
    }

    setIsSubmittingRequest(true);
    try {
      await requestPocketMoney({ amount, reason: requestReason || null });
      toast.success(t('request_sent'));
      setShowRequestModal(false);
      setRequestAmount('');
      setRequestReason('');
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  // Child helper functions
  const getFrequencyLabel = (freq) => {
    const labels = {
      weekly: t('per_week'),
      monthly: t('per_month'),
      manual: t('on_demand')
    };
    return labels[freq] || '';
  };

  const getRequestStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'denied': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-amber-500" />;
    }
  };

  const getRequestStatusLabel = (status) => {
    const labels = {
      pending: t('pending'),
      approved: t('approved'),
      denied: t('refused')
    };
    return labels[status] || status;
  };

  // Calculate income based on budget mode
  // IMPORTANT: We harmonize with the Family page (/api/family/stats -> total_income)
  // which represents the STABLE household income (recurring salaries sum).
  // The Dashboard previously used /api/income/monthly-total -> effective.total which also
  // includes daily/one-time amounts, causing a mismatch.
  const getDisplayedIncome = () => {
    // In family + shared mode: align with Family page (familyStats.total_income)
    if (family && family.budget_mode === 'shared' && familyStats && typeof familyStats.total_income === 'number') {
      return familyStats.total_income;
    }

    // In family + hybrid mode: keep the pot commun target if configured
    if (family && family.budget_mode === 'hybrid') {
      if (family.pot_commun_monthly_target > 0) {
        return family.pot_commun_monthly_target;
      }
      // Fallback to total household income (same as Family page)
      if (familyStats && typeof familyStats.total_income === 'number') {
        return familyStats.total_income;
      }
    }

    // Separate mode or no family: personal income from multi-source system
    const effectiveTotal = monthlyIncome && monthlyIncome.effective && typeof monthlyIncome.effective.total === 'number'
      ? monthlyIncome.effective.total
      : null;
    if (effectiveTotal !== null && effectiveTotal > 0) {
      return effectiveTotal;
    }

    // Final legacy fallback
    if (!family) {
      return user?.salary || 0;
    }
    const totalLegacy = (family.members || [])
      .filter(m => m.role === 'owner' || m.role === 'adult')
      .reduce((sum, m) => sum + (m.salary || 0), 0);
    return totalLegacy > 0 ? totalLegacy : user?.salary || 0;
  };
  
  // Get label for income display based on budget mode
  const getIncomeLabel = () => {
    if (!family) return t('monthly_income');
    
    switch (family.budget_mode) {
      case 'shared':
        return t('household_income');
      case 'hybrid':
        return t('monthly_common_pot');
      case 'separate':
      default:
        return t('personal_income');
    }
  };

  const displayedIncome = getDisplayedIncome();
  // Use familyStats for accurate balance when available, otherwise calculate from displayedIncome
  const availableBalance = familyStats?.available_balance !== undefined 
    ? familyStats.available_balance 
    : displayedIncome - (stats?.total_spending_this_month || 0);
  // Use familyStats for total spending if available
  const totalSpendingThisMonth = familyStats?.total_spending !== undefined
    ? familyStats.total_spending
    : stats?.total_spending_this_month || 0;
  const xpInfo = getXPForNextLevel(user?.xp || 0);
  const isSharedBudget = family && (family.budget_mode === 'shared' || family.budget_mode === 'hybrid');
  const incomeLabel = getIncomeLabel();
  
  // Child balance status for visual feedback
  const childBalanceStatus = pocketMoneyBalance <= 0 ? 'empty' : pocketMoneyBalance < 10 ? 'low' : 'good';

  const handleQuickAddSuccess = async () => {
    console.log('[Dashboard] handleQuickAddSuccess called - refreshing data...');
    setShowQuickAdd(false);
    setShowOCR(false);
    try {
      await loadData(true); // true = refresh mode, don't show loading spinner
      console.log('[Dashboard] Data refreshed successfully after transaction');
    } catch (err) {
      console.error('[Dashboard] Failed to refresh data:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <img 
            src="/spenspot-icon.png" 
            alt="SpenSpot" 
            className="w-16 h-16 animate-pulse mx-auto mb-4 rounded-xl"
          />
          <p className="text-muted-foreground">{t('loading')}</p>
        </div>
      </div>
    );
  }

  // ============== CHILD DASHBOARD ==============
  if (isChild) {
    return (
      <div className="min-h-screen bg-background pb-24">
        {/* Child Header with Balance */}
        <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6 rounded-b-3xl shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-primary-foreground/80 text-sm">
                {t('hello')} 👋
              </p>
              <h1 className="text-2xl font-bold">{user?.name?.split(' ')[0] || 'Enfant'}</h1>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSelector variant="compact" className="text-white" />
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/settings')}
                className="text-white hover:bg-white/20"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={logout}
                className="text-white hover:bg-white/20"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
          
          {/* Balance Card */}
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white/20 backdrop-blur-sm rounded-2xl p-4"
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-primary-foreground/70 text-sm">
                {t('my_pocket_money')}
              </p>
              {/* Privacy toggle for child */}
              <button
                onClick={toggleBalanceVisibility}
                className="p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                title={hideBalances 
                  ? t('show_amounts')
                  : t('hide_amounts')
                }
                data-testid="child-toggle-balance-visibility"
              >
                {hideBalances ? (
                  <EyeOff className="w-4 h-4 text-primary-foreground/70" />
                ) : (
                  <Eye className="w-4 h-4 text-primary-foreground/70" />
                )}
              </button>
            </div>
            <p className={`text-4xl font-bold ${childBalanceStatus !== 'good' ? 'animate-pulse' : ''}`} data-testid="child-balance-amount">
              {maskAmount(pocketMoneyBalance)}
            </p>
            
            {pocketMoneyAllowance && allowanceFrequency !== 'manual' && (
              <p className="text-primary-foreground/70 text-xs mt-2">
                {t('you_receive')} {maskAmount(pocketMoneyAllowance)} {getFrequencyLabel(allowanceFrequency)}
              </p>
            )}
            
            {childBalanceStatus === 'low' && !hideBalances && (
              <p className="text-amber-200 text-xs mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {t('pocket_money_low')}
              </p>
            )}
            
            {childBalanceStatus === 'empty' && !hideBalances && (
              <p className="text-red-200 text-xs mt-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {t('no_pocket_money_ask_parents')}
              </p>
            )}
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="px-4 -mt-4 flex gap-3">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowRequestModal(true)}
            className="flex-1 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg flex items-center justify-center gap-2 text-primary font-medium"
            data-testid="child-request-money-btn"
          >
            <Gift className="w-5 h-5" />
            {t('request_money')}
          </motion.button>
          
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowQuickAdd(true)}
            className="flex-1 bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg flex items-center justify-center gap-2 text-primary font-medium"
            data-testid="child-add-expense-btn"
          >
            <Plus className="w-5 h-5" />
            {t('i_spent')}
          </motion.button>
        </div>

        {/* Quick Actions - Voice, Scan, etc. */}
        <div className="px-4 mt-4 flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVoice(true)}
            className="flex-1 gap-2"
            data-testid="child-voice-btn"
          >
            <Mic className="w-4 h-4" />
            {t('voice')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowOCR(true)}
            className="flex-1 gap-2"
            data-testid="child-scan-btn"
          >
            <Camera className="w-4 h-4" />
            {t('scanner')}
          </Button>
        </div>

        {/* Pending Requests */}
        {pocketMoneyRequests.filter(r => r.status === 'pending').length > 0 && (
          <div className="px-4 mt-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              {t('my_pending_requests')}
            </h2>
            <div className="space-y-2">
              {pocketMoneyRequests.filter(r => r.status === 'pending').map((req) => (
                <motion.div
                  key={req.request_id}
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    {getRequestStatusIcon(req.status)}
                    <div>
                      <p className="font-medium">{formatCurrency(req.amount)}</p>
                      {req.reason && <p className="text-xs text-muted-foreground">{req.reason}</p>}
                    </div>
                  </div>
                  <span className="text-xs text-amber-500 bg-amber-100 dark:bg-amber-900/30 px-2 py-1 rounded-full">
                    {getRequestStatusLabel(req.status)}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-destructive" />
              {t('my_expenses')}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/transactions')}>
              {t('see_all')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          {transactions.length === 0 ? (
            <Card className="bg-white/50 dark:bg-gray-800/50">
              <CardContent className="py-8 text-center">
                <ShoppingBag className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  {t('no_expenses_yet')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {transactions.slice(0, 10).map((txn, idx) => {
                const catKey = (txn.category || 'autre').toLowerCase();
                const category = CHILD_CATEGORIES[catKey] || CHILD_CATEGORIES['autre'];
                const Icon = category.icon;
                
                return (
                  <motion.div
                    key={txn.transaction_id || idx}
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="bg-white dark:bg-gray-800 rounded-xl p-3 shadow flex items-center gap-3"
                  >
                    <div className={`w-10 h-10 rounded-full ${category.bg} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${category.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{txn.description || category.label}</p>
                      <p className="text-xs text-muted-foreground">{formatDateRelative(txn.date, language)}</p>
                    </div>
                    <p className="font-bold text-destructive">-{formatCurrency(txn.amount)}</p>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Family Section - hidden for single users */}
        {family && !shouldHideFamily && (
          <div className="px-4 mt-6">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              {t('my_family')}
            </h2>
            <Card className="bg-white/50 dark:bg-gray-800/50">
              <CardContent className="py-4">
                <div className="flex flex-wrap gap-3">
                  {family.members?.filter(m => m.role !== 'child').map((member, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-primary/10 rounded-full px-3 py-1">
                      <Users className="w-4 h-4 text-primary" />
                      <span className="text-sm">{member.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Request Money Modal */}
        <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-primary" />
                {t('request_money')}
              </DialogTitle>
              <DialogDescription>
                {language === 'fr' 
                  ? 'Ta demande sera envoyée à tes parents'
                  : language === 'pt'
                  ? 'O teu pedido será enviado aos teus pais'
                  : 'Your request will be sent to your parents'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('request_amount')}
                </label>
                <Input
                  type="number"
                  placeholder="0.00"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  className="text-2xl text-center"
                  data-testid="request-amount-input"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('what_for_optional')}
                </label>
                <Input
                  placeholder={t('example_video_game')}
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  data-testid="request-reason-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRequestModal(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleRequestPocketMoney}
                disabled={!requestAmount || parseFloat(requestAmount) <= 0 || isSubmittingRequest}
                className="bg-primary hover:bg-primary/90"
                data-testid="submit-request-btn"
              >
                {isSubmittingRequest ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('send')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Lazy loaded modals */}
        <Suspense fallback={null}>
          {/* Quick Add Modal - works for children too */}
          {showQuickAdd && (
            <QuickAddModal
              open={showQuickAdd}
              onClose={() => setShowQuickAdd(false)}
              onSuccess={handleQuickAddSuccess}
              defaultType="expense"
            />
          )}
          
          {/* OCR Scanner */}
          {showOCR && (
            <OCRScannerModal
              open={showOCR}
              onClose={() => setShowOCR(false)}
              onSuccess={handleQuickAddSuccess}
            />
          )}
          
          {/* Voice Assistant */}
          {showVoice && (
            <VoiceAssistant
              onClose={() => setShowVoice(false)}
              onSuccess={handleQuickAddSuccess}
            />
          )}
        </Suspense>

        {/* Bottom Navigation */}
        <BottomNav />
      </div>
    );
  }

  // ============== ADULT DASHBOARD ==============
  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="/spenspot-icon.png" 
                alt="SpenSpot" 
                className="w-10 h-10 rounded-xl"
              />
              <div>
                <h1 className="font-bold text-lg">
                  {t('hi')}, {user?.name?.split(' ')[0]} 👋
                </h1>
                <p className="text-sm text-muted-foreground">{t('dashboard')}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Admin Button - only for admins */}
              {user?.admin_role && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate('/admin')}
                  className="text-primary"
                  title="Administration"
                >
                  <Shield className="w-5 h-5" />
                </Button>
              )}
              <LanguageSelector variant="compact" />
              <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} data-testid="settings-btn">
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bento-grid">
          {/* Balance Card(s) - Different layout for Hybrid mode */}
          {family?.budget_mode === 'hybrid' ? (
            <>
              {/* Hybrid Mode: Two balance cards side by side */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="col-span-full md:col-span-1"
              >
                <Card className="bg-gradient-to-br from-green-600 to-green-700 border-0" data-testid="pot-commun-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-white/80" />
                        <p className="text-white/80 text-sm font-medium">
                          {t('common_pot')}
                        </p>
                      </div>
                      <button
                        onClick={toggleBalanceVisibility}
                        className="p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                      >
                        {hideBalances ? <EyeOff className="w-4 h-4 text-white/70" /> : <Eye className="w-4 h-4 text-white/70" />}
                      </button>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2" data-testid="pot-commun-balance">
                      {maskAmount(familyStats?.pot_commun_available ?? familyStats?.available_balance ?? 0)}
                    </h2>
                    <div className="text-white/60 text-sm space-y-1">
                      <p>
                        {t('spent')}: {maskAmount(familyStats?.common_spending ?? 0)}
                      </p>
                      <p className="text-white/50">
                        {t('initial_colon')}: {maskAmount(familyStats?.pot_commun_balance ?? 0)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="col-span-full md:col-span-1"
              >
                <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0" data-testid="personal-balance-card">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <User className="w-5 h-5 text-white/80" />
                        <p className="text-white/80 text-sm font-medium">
                          {t('personal')}
                        </p>
                      </div>
                    </div>
                    <h2 className="text-3xl font-bold text-white mb-2" data-testid="personal-balance">
                      {maskAmount((user?.salary ?? 0) - (familyStats?.monthly_contributions ?? 0) - (familyStats?.personal_spending ?? 0))}
                    </h2>
                    <div className="text-white/60 text-sm space-y-1">
                      <p>
                        {t('spent')}: {maskAmount(familyStats?.personal_spending ?? 0)}
                      </p>
                      <p className="text-white/50">
                        {t('budgets')}: {maskAmount((user?.salary ?? 0) - (familyStats?.monthly_contributions ?? 0))}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </>
          ) : (
            /* Standard Balance Card for Shared/Separate modes */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="col-span-full md:col-span-2"
            >
              <Card className={`balance-card ${availableBalance >= 0 ? 'positive' : 'negative'}`} data-testid="balance-card">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {/* Ligne 1: Solde disponible */}
                      <p className="text-white/70 text-sm mb-1">{t('available_balance')}</p>
                      
                      {/* Ligne 2: Montant */}
                      <h2 className="text-4xl font-bold text-white mb-2" data-testid="balance-amount">
                        {maskAmount(availableBalance)}
                      </h2>
                      
                      {/* Ligne 3: Type budget + Pourcentage + Oeil */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {isSharedBudget && (
                          <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 text-white/80 flex items-center gap-1">
                            <Users className="w-3 h-3 flex-shrink-0" />
                            {family?.budget_mode === 'shared' 
                              ? t('shared')
                              : t('household')
                            }
                          </span>
                        )}
                        {family?.budget_mode === 'hybrid' && (
                          <span className="text-xs bg-white/20 rounded-full px-2 py-0.5 text-white/80">
                            🔄 {t('hybrid_label')}
                          </span>
                        )}
                        <div className="flex items-center gap-1 bg-white/10 rounded-full px-2 py-0.5">
                          {availableBalance >= 0 ? (
                            <TrendingUp className="w-3 h-3 text-green-300" />
                          ) : (
                            <TrendingDown className="w-3 h-3 text-red-300" />
                          )}
                          <span className="text-xs font-medium text-white">
                            {hideBalances ? '••%' : `${availableBalance >= 0 ? '+' : ''}${calculatePercentage(totalSpendingThisMonth, displayedIncome || 1)}%`}
                          </span>
                        </div>
                        <button
                          onClick={toggleBalanceVisibility}
                          className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                          title={hideBalances ? t('show_amounts') : t('hide_amounts')}
                          data-testid="toggle-balance-visibility"
                        >
                          {hideBalances ? (
                            <EyeOff className="w-4 h-4 text-white/70" />
                          ) : (
                            <Eye className="w-4 h-4 text-white/70" />
                          )}
                        </button>
                      </div>
                      
                      {/* Ligne 4: Infos supplémentaires */}
                      <div className="text-white/60 text-sm mt-3 space-y-1">
                        <p>
                          {t('this_month')}: {maskAmount(totalSpendingThisMonth)} {t('spent')}
                        </p>
                        {family && (
                          <p className="text-white/50">
                            {incomeLabel}: {maskAmount(displayedIncome)}/{t('month')}
                          </p>
                        )}
                        {/* Breakdown fixed vs variable income (only when both exist) */}
                        {(() => {
                          const fixedIncome = Number(familyStats?.total_salaries ?? user?.salary ?? 0) || 0;
                          const totalForBreakdown = Number(displayedIncome || 0);
                          const variableIncome = Math.max(0, totalForBreakdown - fixedIncome);
                          if (variableIncome > 0 && fixedIncome > 0) {
                            return (
                              <button
                                type="button"
                                onClick={() => setShowVarBreakdown(true)}
                                className="text-white/50 text-xs flex items-center gap-2 flex-wrap hover:text-white/80 transition-colors group"
                                data-testid="income-breakdown"
                                title={t('view_variable_income_details')}
                              >
                                <span className="inline-flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-white/60" />
                                  {t('fixed_income_label')}: {maskAmount(fixedIncome)}
                                </span>
                                <span className="inline-flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                                  {t('variable_income_label')}: +{maskAmount(variableIncome)}
                                </span>
                                <span className="opacity-0 group-hover:opacity-100 text-[10px] underline transition-opacity">
                                  {t('details')}
                                </span>
                              </button>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Variable Income Nudge - shown for non-employed users (freelance/student/unemployed) */}
          {!isChild && user?.income_type && user.income_type !== 'employed' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="col-span-full"
            >
              <Card className="bg-gradient-to-br from-emerald-600 to-teal-700 border-0 overflow-hidden" data-testid="variable-income-nudge">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-white font-bold text-base">{t('variable_income_nudge_title')}</h3>
                        <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
                          {t(`status_${user.income_type}`)}
                        </span>
                      </div>
                      <p className="text-white/80 text-sm mb-3">{t('variable_income_nudge_desc')}</p>
                      {monthlyIncome?.personal?.total > 0 && (
                        <p className="text-white/90 text-sm mb-3">
                          <span className="opacity-70">{t('projected_this_month')}:</span>{' '}
                          <span className="font-bold">{maskAmount(monthlyIncome.personal.total)}</span>
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => setShowQuickAddIncome(true)}
                          className="bg-white text-emerald-700 hover:bg-white/90 transition-colors px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                          data-testid="nudge-log-income-btn"
                        >
                          <Plus className="w-4 h-4" />
                          {t('log_income_now')}
                        </button>
                        <button
                          onClick={() => navigate('/settings?openIncomeSources=1')}
                          className="bg-white/15 text-white hover:bg-white/25 transition-colors px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                          data-testid="nudge-manage-income-btn"
                        >
                          <Wallet className="w-4 h-4" />
                          {t('manage_income_sources')}
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Income evolution chart - only for non-employed users */}
          {!isChild && user?.income_type && user.income_type !== 'employed' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.09 }}
              className="col-span-full md:col-span-2"
            >
              <Suspense fallback={null}>
                <IncomeEvolutionChart months={12} scope="personal" />
              </Suspense>
            </motion.div>
          )}

          {/* XP & Streak */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="h-full" data-testid="xp-card">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-primary" />
                    <span className="font-medium">{t('level')} {user?.level || 1}</span>
                  </div>
                  <div className="flex items-center gap-1 text-orange-500">
                    <Flame className="w-5 h-5" />
                    <span className="font-bold">{user?.streak_days || 0}</span>
                  </div>
                </div>
                <Progress value={(xpInfo.current / xpInfo.required) * 100} className="h-2 mb-2" />
                <p className="text-xs text-muted-foreground">
                  {xpInfo.current}/{xpInfo.required} XP
                </p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Daily Challenge */}
          {challenge && !challenge.completed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="col-span-full md:col-span-2"
            >
              <ChallengeCard challenge={challenge} onComplete={() => loadData(true)} />
            </motion.div>
          )}

          {/* Alerts Panel */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="col-span-full"
          >
            <AlertsPanel onRefresh={loadData} />
          </motion.div>

          {/* Negative Balance Modal - shows automatically if there's a pending carryover */}
          <NegativeBalanceModal onResolved={loadData} />

          {/* Budget Progress */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="col-span-full md:col-span-2"
          >
            <Card data-testid="budget-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('budget_progress')}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/budgets')} data-testid="view-budgets-btn">
                    {t('view_all')} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {budgets.length > 0 ? (
                  <div className="space-y-4">
                    {budgets.slice(0, 4).map((budget) => (
                      <div key={budget.budget_id}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{t(`cat_${(budget.category || 'autre').toLowerCase()}`)}</span>
                          <span className={budget.percentage > 100 ? 'text-red-500' : 'text-muted-foreground'}>
                            {formatCurrency(budget.spent)} / {formatCurrency(budget.amount)}
                          </span>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all duration-500"
                            style={{
                              width: `${Math.min(100, budget.percentage)}%`,
                              backgroundColor: budget.percentage > 100 ? '#EF4444' : getCategoryColor(budget.category)
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <PiggyBank className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground mb-4">
                      {t('no_budgets_configured')}
                    </p>
                    <Button onClick={() => navigate('/budgets')} data-testid="setup-budget-btn">
                      {t('set_budget')}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Objectives */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="h-full" data-testid="objectives-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('objectives')}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/objectives')} data-testid="view-objectives-btn">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {objectives.length > 0 ? (
                  <div className="space-y-3">
                    {objectives.slice(0, 2).map((obj) => (
                      <div key={obj.objective_id} className="bg-muted/50 rounded-xl p-3">
                        <div className="flex items-center gap-2 mb-2 min-w-0">
                          <Target className="w-4 h-4 text-primary flex-shrink-0" />
                          <span className="font-medium text-sm truncate min-w-0">{obj.name}</span>
                        </div>
                        <Progress value={obj.progress_percentage} className="h-1.5 mb-1" />
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>{formatCurrency(obj.current_amount)}</span>
                          <span>{formatCurrency(obj.target_amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Target className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      {t('no_objectives')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Tools Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.48 }}
            className="col-span-full"
          >
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  {t('tools')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {/* Analytics */}
                  <button
                    onClick={() => navigate('/analytics')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/20 dark:hover:bg-blue-950/40 transition-colors"
                    data-testid="analytics-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center">
                      <PieChart className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('charts')}
                    </span>
                  </button>

                  {/* Recurring */}
                  <button
                    onClick={() => navigate('/recurring')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/20 dark:hover:bg-indigo-950/40 transition-colors"
                    data-testid="recurring-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-indigo-500 text-white flex items-center justify-center">
                      <RefreshCw className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('recurring')}
                    </span>
                  </button>

                  {/* Expense Simulator */}
                  {stats?.features?.expense_simulator && (
                    <button
                      onClick={() => setShowSimulator(true)}
                      className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 dark:from-blue-950/20 dark:to-indigo-950/20 dark:hover:from-blue-950/40 dark:hover:to-indigo-950/40 transition-colors"
                      data-testid="simulator-tool"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-white flex items-center justify-center">
                        <Calculator className="w-5 h-5" />
                      </div>
                      <span className="text-xs font-medium text-center">
                        {language === 'fr' ? 'Simulateur' : language === 'pt' ? 'Simulador' : 'Simulator'}
                      </span>
                    </button>
                  )}

                  {/* Shopping Intelligent - Hidden (Backlog)
                  <button
                    onClick={() => navigate('/shopping')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-green-50 hover:bg-green-100 dark:bg-green-950/20 dark:hover:bg-green-950/40 transition-colors"
                    data-testid="shopping-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center">
                      <ShoppingCart className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('shopping')}
                    </span>
                  </button>
                  */}

                  {/* Email Scan - Disabled (not relevant)
                  <button
                    onClick={() => navigate('/email-scan')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-purple-50 hover:bg-purple-100 dark:bg-purple-950/20 dark:hover:bg-purple-950/40 transition-colors"
                    data-testid="email-scan-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center">
                      <Mail className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('emails')}
                    </span>
                  </button>
                  */}

                  {/* Export Fiscal */}
                  <button
                    onClick={() => navigate('/export')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/20 dark:hover:bg-amber-950/40 transition-colors"
                    data-testid="export-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center">
                      <FileText className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('export')}
                    </span>
                  </button>

                  {/* Monthly Reports */}
                  <button
                    onClick={() => navigate('/reports')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-cyan-50 hover:bg-cyan-100 dark:bg-cyan-950/20 dark:hover:bg-cyan-950/40 transition-colors"
                    data-testid="reports-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-cyan-500 text-white flex items-center justify-center">
                      <FileBarChart className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('reports')}
                    </span>
                  </button>

                  {/* AI Predictions */}
                  <button
                    onClick={() => navigate('/predictions')}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl bg-violet-50 hover:bg-violet-100 dark:bg-violet-950/20 dark:hover:bg-violet-950/40 transition-colors"
                    data-testid="predictions-tool"
                  >
                    <div className="w-10 h-10 rounded-full bg-violet-500 text-white flex items-center justify-center">
                      <Zap className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-medium text-center">
                      {t('ai_predictions')}
                    </span>
                  </button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Recent Transactions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="col-span-full"
          >
            <Card data-testid="transactions-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{t('recent_transactions')}</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => navigate('/transactions')} data-testid="view-transactions-btn">
                    {t('view_all')} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {transactions.length > 0 ? (
                  <div className="divide-y divide-border">
                    {transactions.map((txn) => (
                      <div key={txn.transaction_id} className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${getCategoryColor(txn.category)}20` }}
                          >
                            <span className="text-lg">
                              {txn.category === 'alimentation' ? '🍔' :
                               txn.category === 'transport' ? '🚗' :
                               txn.category === 'shopping' ? '🛍️' :
                               txn.category === 'loisirs' ? '🎮' :
                               txn.category === 'logement' ? '🏠' : '💰'}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{txn.description || t(`cat_${(txn.category || 'autre').toLowerCase()}`)}</p>
                            <p className="text-xs text-muted-foreground">{formatDateRelative(txn.date, language)}</p>
                          </div>
                        </div>
                        <span className="font-bold text-destructive">-{formatCurrency(txn.amount)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Wallet className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-muted-foreground">{t('no_transactions')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </main>

      {/* FAB Menu - Collapsible */}
      <div className="fixed bottom-24 md:bottom-6 right-4 z-50">
        <AnimatePresence>
          {fabExpanded && (
            <>
              {/* Backdrop to close menu */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
                onClick={() => setFabExpanded(false)}
              />
              
              {/* Expanded FAB buttons */}
              <motion.div 
                className="absolute bottom-20 right-0 flex flex-col items-end gap-3 z-50"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.2 }}
              >
                {/* AI Chat Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.1 }}
                  className="flex items-center gap-2"
                >
                  <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                    {t('ai_assistant')}
                  </span>
                  <button
                    onClick={() => { navigate('/chat'); setFabExpanded(false); }}
                    className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 shadow-lg flex items-center justify-center hover:from-emerald-600 hover:to-cyan-600 transition-colors"
                    data-testid="chat-fab"
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </button>
                </motion.div>
                
                {/* Voice Input Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.15 }}
                  className="flex items-center gap-2"
                >
                  <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                    {t('voice_input_label')}
                  </span>
                  <button
                    onClick={() => { setShowVoice(true); setFabExpanded(false); }}
                    className="w-12 h-12 rounded-full bg-card border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-colors"
                    data-testid="voice-fab"
                  >
                    <Mic className="w-5 h-5 text-purple-500" />
                  </button>
                </motion.div>
                
                {/* OCR Scanner Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.2 }}
                  className="flex items-center gap-2"
                >
                  <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                    {t('scan_receipt_label')}
                  </span>
                  <button
                    onClick={() => { setShowOCR(true); setFabExpanded(false); }}
                    className="w-12 h-12 rounded-full bg-card border border-border shadow-lg flex items-center justify-center hover:bg-muted transition-colors"
                    data-testid="ocr-fab"
                  >
                    <Camera className="w-5 h-5 text-primary" />
                  </button>
                </motion.div>
                
                {/* Expense Simulator Button */}
                {stats?.features?.expense_simulator && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: 0.22 }}
                    className="flex items-center gap-2"
                  >
                    <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                      {language === 'fr' ? 'Simulateur' : language === 'pt' ? 'Simulador' : 'Simulator'}
                    </span>
                    <button
                      onClick={() => { setShowSimulator(true); setFabExpanded(false); }}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg flex items-center justify-center hover:from-blue-600 hover:to-indigo-600 transition-colors"
                      data-testid="simulator-fab"
                    >
                      <Calculator className="w-5 h-5 text-white" />
                    </button>
                  </motion.div>
                )}
                
                {/* Quick Add Income FAB - only shown for users with variable/daily/weekly income sources */}
                {monthlyIncome?.personal?.has_variable_income && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: 0.23 }}
                    className="flex items-center gap-2"
                  >
                    <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                      {t('quick_add_income')}
                    </span>
                    <button
                      onClick={() => { setShowQuickAddIncome(true); setFabExpanded(false); }}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg flex items-center justify-center hover:from-emerald-600 hover:to-teal-600 transition-colors"
                      data-testid="quick-add-income-fab"
                    >
                      <TrendingUp className="w-5 h-5 text-white" />
                    </button>
                  </motion.div>
                )}
                
                {/* Quick Add Button */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: 0.25 }}
                  className="flex items-center gap-2"
                >
                  <span className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full whitespace-nowrap">
                    {t('add_expense_label')}
                  </span>
                  <button
                    onClick={() => { setShowQuickAdd(true); setFabExpanded(false); }}
                    className="w-12 h-12 rounded-full bg-primary text-white shadow-lg flex items-center justify-center hover:bg-primary/90 transition-colors"
                    data-testid="quick-add-fab"
                  >
                    <Plus className="w-6 h-6" />
                  </button>
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
        
        {/* Main FAB Toggle Button */}
        <motion.button
          onClick={() => setFabExpanded(!fabExpanded)}
          className={`w-16 h-16 rounded-full shadow-lg flex items-center justify-center transition-all z-50 relative ${
            fabExpanded 
              ? 'bg-zinc-800 text-white' 
              : 'bg-primary text-white'
          }`}
          style={{ boxShadow: fabExpanded ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(255, 79, 0, 0.4)' }}
          data-testid="fab-toggle"
          animate={{ rotate: fabExpanded ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {fabExpanded ? (
            <X className="w-7 h-7" />
          ) : (
            <Plus className="w-7 h-7" />
          )}
        </motion.button>
        
        {/* Indicator dots when collapsed */}
        {!fabExpanded && (
          <motion.div 
            className="absolute -top-1 -left-1 flex gap-0.5"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <span className="w-2 h-2 rounded-full bg-emerald-500" title="IA" />
            <span className="w-2 h-2 rounded-full bg-purple-500" title="Voice" />
            <span className="w-2 h-2 rounded-full bg-primary" title="Camera" />
          </motion.div>
        )}
      </div>

      {/* Lazy loaded modals wrapped in Suspense */}
      <Suspense fallback={null}>
        {/* Quick Add Modal */}
        {showQuickAdd && (
          <QuickAddModal 
            open={showQuickAdd} 
            onClose={() => setShowQuickAdd(false)}
            onSuccess={handleQuickAddSuccess}
          />
        )}

        {/* Quick Add Income Modal (only loaded when triggered) */}
        {showQuickAddIncome && (
          <QuickAddIncomeModal
            open={showQuickAddIncome}
            onOpenChange={setShowQuickAddIncome}
            onSaved={() => loadData(true)}
          />
        )}

        {/* Variable income drill-down modal */}
        {showVarBreakdown && (
          <VariableIncomeBreakdownModal
            open={showVarBreakdown}
            onOpenChange={setShowVarBreakdown}
          />
        )}

        {/* OCR Scanner Modal */}
        {showOCR && (
          <OCRScannerModal
            open={showOCR}
            onClose={() => setShowOCR(false)}
            onSuccess={handleQuickAddSuccess}
          />
        )}

        {/* Voice Assistant */}
        {showVoice && (
          <VoiceAssistant
            isOpen={showVoice}
            onClose={() => setShowVoice(false)}
            onSuccess={() => loadData(true)}
          />
        )}

        {/* Expense Simulator */}
        {showSimulator && (
          <ExpenseSimulator
            isOpen={showSimulator}
            onClose={() => setShowSimulator(false)}
          />
        )}

        {/* Onboarding Tutorial */}
        {tutorialActive && (
          <OnboardingTutorial onComplete={() => setTutorialActive(false)} />
        )}
      </Suspense>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
